// Model Tag Converter - Handles template tag replacement for car model data
import { yearString } from './constants';
import { dateFormat, formatPrice, formatPriceRange } from './helper';
import { EMI_DEFAULT_INTEREST_RATE, EMI_DEFAULT_TENURE, formatEmiCurrency, getDefaultDownPayment } from '@/lib/utils/emi';
import { getOnRoadPrice } from '@/utils/onRoadPrice';

import type { SpecSection } from "./interfaces";

interface CarVariant {
	name?: string;
	price?: number;
	fuel_type?: string;
	transmission?: string | null;
	is_electric?: boolean;
	specSections?: SpecSection[];
}

interface CarModel {
	brand?: { name: string };
	brand_name?: string;
	h1_title?: string;
	name?: string;
	model_name?: string;
	price_range?: { base?: number | string; top?: number | string };
	price?: number | string;
	variants?: CarVariant[];
	is_electric?: boolean;
}

/**
 * Find spec value by exact key (case-insensitive) from variant's specSections
 * 
 * @param variant - Car variant with specSections array
 * @param key - Spec key to search for
 * @returns Spec value as string or empty string if not found
 */
export const findSpecValue = (variant: CarVariant | any, key: string): string => {
	if (!variant) return '';

	const targetKey = key.toLowerCase().trim();

	// Check specSections
	if (Array.isArray(variant.specSections)) {
		for (const section of variant.specSections) {
			if (!Array.isArray(section.specs)) continue;
			for (const spec of section.specs as any[]) {
				if (spec.key?.toLowerCase().trim() === targetKey) {
					if (spec.value !== undefined && spec.value !== null && spec.value !== '') {
						return String(spec.value).trim();
					}
					if (spec.valueNormalized !== undefined && spec.valueNormalized !== null && spec.valueNormalized !== '') {
						return `${spec.valueNormalized}${spec.unit ? ` ${spec.unit}` : ''}`.trim();
					}
					return '';
				}
			}
		}
	}

	// Check featureSections
	if (Array.isArray(variant.featureSections)) {
		for (const section of variant.featureSections) {
			if (!Array.isArray(section.features)) continue;
			for (const feat of section.features as any[]) {
				if (feat.key?.toLowerCase().trim() === targetKey) {
					if (feat.available !== undefined && feat.available !== null && feat.available !== '') {
						return String(feat.available).trim();
					}
					if (feat.value !== undefined && feat.value !== null && feat.value !== '') {
						return String(feat.value).trim();
					}
					return '';
				}
			}
		}
	}

	// Direct property lookup
	if (variant[targetKey] !== undefined && variant[targetKey] !== null && variant[targetKey] !== '') {
		return String(variant[targetKey]).trim();
	}

	return '';
};

/**
 * Converts template tags to actual values based on model data.
 * Supports multiple content types: simple strings, FAQs, single-section, and multi-section content.
 * 
 * @param content - Template string or object with tags to replace
 * @param model - Model data object containing variant information
 * @param options - Configuration options
 * @param options.type - Content type: 'simple' | 'faq' | 'multi-section' | 'single-section' | 'auto'
 * @param options.fallback - Fallback text when tags are missing (for FAQs)
 * @param options.removeMissingSentences - Remove sentences with unfilled tags (for location routes)
 * @returns Processed string
 * 
 * @example
 * // Simple usage
 * convertModelTags(model.h1_title, model)
 * 
 * @example
 * // FAQ with fallback
 * convertModelTags(faq.answer, model, { fallback: faq.fallback })
 * 
 * @example
 * // Multi-section content
 * convertModelTags(modelDynamicTopContent.EV, model)
 * 
 * @example
 * // Location route with sentence removal
 * convertModelTags(modelLocationTopContent.nonEV, model, { removeMissingSentences: true })
 */
export function convertModelTags(
	content: any,
	model: any,
	options?: {
		type?: 'simple' | 'faq' | 'multi-section' | 'single-section' | 'location' | 'auto';
		fallback?: string;
		removeMissingSentences?: boolean;
	}
): string {
	if (!content || !model) return '';

	const {
		type = 'auto',
		fallback,
		removeMissingSentences = false
	} = options || {};

	//=============================================
	// Extract location and calculate RTO and insurance
	//=============================================
	const locationInfo = model.locationInfo || {};

	let emi = 0;
	let emiCarPrice = 0;
	let hasOnRoadCharges = false;
	let minDownPayment = 0;
	let insuranceTotal = 0;
	let rtoTotal = 0;
	let loanAmount = 0;

	// model.base_variant is never populated by /model/detail — fall back to a
	// real fetched variant when present, else a shim built from modelObj itself
	// (which carries specSections/fuel_types/price_range directly).
	const rtoBaseVariant = model?.base_variant
		|| (Array.isArray(model?.variants) ? model.variants[0] : null)
		|| (model?.price_range?.base ? { ...model, price: model.price_range.base } : null);

	if (locationInfo && rtoBaseVariant) {
		const baseVariant = Array.isArray(rtoBaseVariant) ? rtoBaseVariant[0] : rtoBaseVariant;
		const result = getOnRoadPrice(baseVariant, locationInfo);
		if (result) {
			emiCarPrice = result.onRoadPrice;
			rtoTotal = result.rtoEstimate?.total || 0;
			insuranceTotal = result.insuranceEstimate?.total || 0;
			hasOnRoadCharges = Boolean(result.rtoEstimate && result.insuranceEstimate);
			minDownPayment = getDefaultDownPayment(emiCarPrice);
			emi = result.defaultEmi;
			loanAmount = emiCarPrice - minDownPayment;
		}
	}

	const min_price_variant = Array.isArray(model?.variants) ? model.variants.reduce((min: any, v: any) => {
		const vPrice = Number(v.price ?? 0);
		if (vPrice < (min.price ?? Infinity)) {
			return v;
		}
		return min;
	}, model.variants[0]) : null;

	let min_onRoad_price = 0;

	if(min_price_variant && locationInfo) {
		const minPriceVariantOnRoad = getOnRoadPrice(min_price_variant, locationInfo);
		if(minPriceVariantOnRoad) {
			min_onRoad_price = minPriceVariantOnRoad.onRoadPrice || 0;
		}
	}

	// ============================================
	// STEP 1: Extract all model data
	// ============================================
	const brand = model.brand?.name || model.brand_name || 'this premium car';
	const carName = model.name || model.model_name || 'model';
	const cityName = model.locationInfo?.name || 'your city';
	const allVariants = Array.isArray(model.variants) ? model.variants : [];
	const variantCount = Number(model.variant_count) || allVariants.length || 0;

	const priceRange = model.price_range || null;
	const priceStart = Number(model.price_range?.base ?? model.price ?? 0) || 0;
	const priceEnd = Number(model.price_range?.top ?? model.price ?? 0) || 0;
	
	// model itself carries specSections/featureSections directly (see
	// ModelRepository.findDetailWithLookups) — the last-resort fallback so
	// findSpecValue(primaryVariant, ...) below still resolves real spec data
	// on pages that never populate model.base_variant/model.variants.
	const primaryVariant = model.base_variant || (Array.isArray(model.variants) ? model.variants[0] : null) || model;

	const fuelType = primaryVariant?.fuel_type || findSpecValue(primaryVariant, 'fuel_type');
	const isEV = primaryVariant?.is_electric ?? model.is_electric ?? fuelType?.toLowerCase().includes('electric');

	// Extract all specs
	const engineDisplacement = findSpecValue(primaryVariant, 'engine_displacement');
	const maxPower = findSpecValue(primaryVariant, 'max_power');
	const maxTorque = findSpecValue(primaryVariant, 'max_torque');
	const transmissionType = findSpecValue(primaryVariant, 'transmission_type') || (primaryVariant?.transmission || '').trim();
	const noOfCylinders = findSpecValue(primaryVariant, 'no_of_cylinders');
	const groundClearanceUnladen = findSpecValue(primaryVariant, 'ground_clearance_unladen');
	const groundClearanceLaden = findSpecValue(primaryVariant, 'ground_clearance_laden');

	const araiMileage = findSpecValue(primaryVariant, 'arai_mileage');
	const cityMileage = findSpecValue(primaryVariant, 'city_mileage');
	const wltpMileage = findSpecValue(primaryVariant, 'wltp_mileage');
	const bodyType = findSpecValue(primaryVariant, 'body_type');
	const batteryCapacity = findSpecValue(primaryVariant, 'battery_capacity');
	const range = findSpecValue(primaryVariant, 'range');
	const chargingTime = findSpecValue(primaryVariant, 'charging_time');

	const frontSuspension = findSpecValue(primaryVariant, 'front_suspension');
	const rearSuspension = findSpecValue(primaryVariant, 'rear_suspension');
	const steeringType = findSpecValue(primaryVariant, 'steering_type');
	const frontBrakeType = findSpecValue(primaryVariant, 'front_brake_type');
	const rearBrakeType = findSpecValue(primaryVariant, 'rear_brake_type');
	const minTurningRadius = findSpecValue(primaryVariant, 'minimum_turning_radius');
	const wheelSize = findSpecValue(primaryVariant, 'wheel_size');
	const tyreSize = findSpecValue(primaryVariant, 'tyre_size');

	const length = findSpecValue(primaryVariant, 'length');
	const width = findSpecValue(primaryVariant, 'width');
	const height = findSpecValue(primaryVariant, 'height');
	const wheelBase = findSpecValue(primaryVariant, 'wheel_base');
	const kerbWeight = findSpecValue(primaryVariant, 'kerb_weight');
	const grossWeight = findSpecValue(primaryVariant, 'gross_weight');
	const seatingCapacity = findSpecValue(primaryVariant, 'seating_capacity');
	const bootSpace = findSpecValue(primaryVariant, 'boot_space');
	const noOfDoors = findSpecValue(primaryVariant, 'no_of_doors');
	const fuelTankCapacity = findSpecValue(primaryVariant, 'fuel_tank_capacity');

	// Features
	const powerSteering = findSpecValue(primaryVariant, 'power_steering');
	const powerWindowsFront = findSpecValue(primaryVariant, 'power_windows_front');
	const airConditioner = findSpecValue(primaryVariant, 'air_conditioner');
	const autoClimateControl = findSpecValue(primaryVariant, 'automatic_climate_control');
	const multiFunctionSteering = findSpecValue(primaryVariant, 'multi_function_steering_wheel');
	const engineStartStop = findSpecValue(primaryVariant, 'engine_start_stop_button');
	const tachometer = findSpecValue(primaryVariant, 'tachometer');
	const leatherSeats = findSpecValue(primaryVariant, 'leather_seats');
	const fabricUpholstery = findSpecValue(primaryVariant, 'fabric_upholstery');
	const digitalCluster = findSpecValue(primaryVariant, 'digital_cluster');
	const alloyWheels = findSpecValue(primaryVariant, 'alloy_wheels');
	const fogLightsFront = findSpecValue(primaryVariant, 'fog_lights_front');
	const xenonHeadlamps = findSpecValue(primaryVariant, 'xenon_headlamps');
	const abs = findSpecValue(primaryVariant, 'anti_lock_braking_system_abs');
	const driverAirbag = findSpecValue(primaryVariant, 'driver_airbag');
	const passengerAirbag = findSpecValue(primaryVariant, 'passenger_airbag');
	const noOfAirbags = findSpecValue(primaryVariant, 'no_of_airbags');
	const centralLocking = findSpecValue(primaryVariant, 'central_locking');
	const rearCamera = findSpecValue(primaryVariant, 'rear_camera');
	const radio = findSpecValue(primaryVariant, 'radio');
	const bluetooth = findSpecValue(primaryVariant, 'bluetooth');
	const touchscreen = findSpecValue(primaryVariant, 'touchscreen');
	const touchscreenSize = findSpecValue(primaryVariant, 'touchscreen_size');
	const androidAuto = findSpecValue(primaryVariant, 'android_auto');
	const appleCarplay = findSpecValue(primaryVariant, 'apple_carplay');
	const aeb = findSpecValue(primaryVariant, 'autonomous_emergency_braking');
	const laneDeparture = findSpecValue(primaryVariant, 'lane_departure_warning');
	const adaptiveCruise = findSpecValue(primaryVariant, 'adaptive_cruise_control');
	const connectedCar = findSpecValue(primaryVariant, 'connected_car');
	const otaUpdates = findSpecValue(primaryVariant, 'over_the_air_updates');

	// Location-specific variant data
	// Use model.listing_summary if available, otherwise calculate from variants
	let topVariant: any = null;
	let baseVariant: any = null;
	const uniqueFuelOptions: string[] = Array.from(new Set(
		allVariants
			.map((v: any) => (v?.fuel_type || v?.fuel_types?.[0] || findSpecValue(v, 'fuel_type') || '').toString().trim())
			.filter(Boolean)
			.map((fuel: string) => fuel)
	));

	// comma-separated fuel options
	const fuelOptions = uniqueFuelOptions.join(', ');

	if (model.listing_summary?.overall) {
		// Use pre-synced listing_summary for top and base variants
		topVariant = {
			name: model.listing_summary.overall.expensive?.variant_name || '',
			price: model.listing_summary.overall.expensive?.price || 0
		};
		baseVariant = {
			name: model.listing_summary.overall.cheapest?.variant_name || '',
			price: model.listing_summary.overall.cheapest?.price || 0
		};
	} else if (allVariants && allVariants.length > 0) {
		// Fallback to client-side calculation
		topVariant = allVariants.reduce((max: any, v: any) => {
			const vPrice = Number(v.price ?? 0);
			const maxPrice = Number(max?.price ?? 0);
			return vPrice > maxPrice ? v : max;
		}, allVariants[0]);

		baseVariant = allVariants.reduce((min: any, v: any) => {
			const vPrice = Number(v.price ?? 0);
			const minPrice = Number(min?.price ?? 0);
			return vPrice < minPrice && vPrice > 0 ? v : min;
		}, allVariants[0]);
	}

	// Check if listing_summary is available, otherwise fall back to client-side calculation
	const topVariantFromList = allVariants.length > 0
		? allVariants.reduce((max: any, v: any) => {
			const vPrice = Number(v?.price ?? 0);
			const maxPrice = Number(max?.price ?? 0);
			return vPrice > maxPrice ? v : max;
		}, allVariants[0])
		: null;

	if (!topVariant?.name && topVariantFromList) {
		topVariant = topVariantFromList;
	}

	const topVariantOnRoad = locationInfo && topVariantFromList
		? getOnRoadPrice(topVariantFromList, locationInfo)?.onRoadPrice || 0
		: 0;
	let minAutomaticPrice = 0;
	let maxAutomaticPrice = 0;
	let cngStartingPrice = 0;

	if (model.listing_summary?.transmission?.Automatic?.cheapest) {
		// Use pre-synced listing_summary for automatic variants
		minAutomaticPrice = model.listing_summary.transmission.Automatic.cheapest?.price || 0;
		maxAutomaticPrice = model.listing_summary.transmission.Automatic.expensive?.price || 0;
	} else if (allVariants && allVariants.length > 0) {
		// Fallback to client-side calculation
		const automaticVariants = allVariants.filter((v: any) => {
			const trans = findSpecValue(v, 'transmission_type') || v.transmission || '';
			return trans.toLowerCase().includes('automatic') ||
				trans.toLowerCase().includes('amt') ||
				trans.toLowerCase().includes('cvt') ||
				trans.toLowerCase().includes('dct');
		});

		minAutomaticPrice = automaticVariants.length > 0
			? Math.min(...automaticVariants.map((v: any) => Number(v.price ?? 0)))
			: 0;

		maxAutomaticPrice = automaticVariants.length > 0
			? Math.max(...automaticVariants.map((v: any) => Number(v.price ?? 0)))
			: 0;
	}

	if (model.listing_summary?.fuel?.CNG?.cheapest) {
		// Use pre-synced listing_summary for CNG variants
		cngStartingPrice = model.listing_summary.fuel.CNG.cheapest?.price || 0;
	} else if (allVariants && allVariants.length > 0) {
		// Fallback to client-side calculation
		const cngVariants = allVariants.filter((v: any) => {
			const fuel = v.fuel_type || findSpecValue(v, 'fuel_type') || '';
			return fuel.toLowerCase().includes('cng');
		});

		cngStartingPrice = cngVariants.length > 0
			? Math.min(...cngVariants.map((v: any) => Number(v.price ?? 0)))
			: 0;
	}

	// Add Diesel price extraction
	let dieselStartingPrice = 0;
	if (model.listing_summary?.fuel?.Diesel?.cheapest) {
		// Use pre-synced listing_summary for Diesel variants
		dieselStartingPrice = model.listing_summary.fuel.Diesel.cheapest?.price || 0;
	} else if (allVariants && allVariants.length > 0) {
		// Fallback to client-side calculation
		const dieselVariants = allVariants.filter((v: any) => {
			const fuel = v.fuel_type || findSpecValue(v, 'fuel_type') || '';
			return fuel.toLowerCase().includes('diesel');
		});

		dieselStartingPrice = dieselVariants.length > 0
			? Math.min(...dieselVariants.map((v: any) => Number(v.price ?? 0)))
			: 0;
	}

	// Competitors (top 2 from model object)
	const competitors = Array.isArray(model.competitors) ? model.competitors : [];
	const comp1 = competitors[0] || {};
	const comp2 = competitors[1] || {};

	const comp1Name = comp1.model?.name ? `${comp1.brand?.name || ''} ${comp1.model?.name}`.trim() : '';
	const comp1Price = (comp1.price_range?.base || comp1.price) ? formatPrice(Number(comp1.price_range?.base || comp1.price)) : '';
	const comp2Name = comp2.model?.name ? `${comp2.brand?.name || ''} ${comp2.model?.name}`.trim() : '';
	const comp2Price = (comp2.price_range?.base || comp2.price) ? formatPrice(Number(comp2.price_range?.base || comp2.price)) : '';

	// Formatted values
	let engineSize = '';
	if (!isEV && engineDisplacement) {
		const num = engineDisplacement.replace(/[^\d.]/g, '');
		engineSize = num ? `${num} cc` : engineDisplacement;
	}

	const powerDisplay = maxPower ? maxPower.split('@')[0].trim() : '';

	const brandSlug = model.brand?.slug || model.brand_slug || '';
	const modelSlug = model.slug || model.model_slug || '';

	// ============================================
	// STEP 2: Build comprehensive replacements map
	// ============================================
	const StartingPriceNoSymbol = formatPrice(priceStart, { includeUnit: true }) as string;

	const replacements: Record<string, string> = {

		'{MinEmiAmount}': formatEmiCurrency(emi),
		'{NumberOfMonths}': String(EMI_DEFAULT_TENURE),
		'{InterestRate}': String(EMI_DEFAULT_INTEREST_RATE),
		'{RtoPrice}': hasOnRoadCharges && rtoTotal ? `₹ ${formatEmiCurrency(rtoTotal)}` : '',
		'{MinDownPayment}': minDownPayment ? `₹ ${formatEmiCurrency(minDownPayment)}` : '',
		'{OnRoadPrice}': emiCarPrice ? `₹ ${formatEmiCurrency(emiCarPrice)}` : '',
		'{OnRoadPrice_FORMATTED}': min_onRoad_price ? `${formatPrice(min_onRoad_price, { includeUnit: true })}` : '',
		'{TopVariantOnRoadPrice}': topVariantOnRoad ? `${formatPrice(topVariantOnRoad)}` : '',
		'{InsuranceCharges}': insuranceTotal ? `₹ ${formatEmiCurrency(insuranceTotal)}` : '',
		'{loanAmount}': loanAmount ? `₹ ${formatEmiCurrency(loanAmount)}` : '',

		// Core identifiers
		'{Brand}': brand,
		'{BrandName}': brand,
		'{Model}': carName,
		'{CarName}': carName,
		'{City}': cityName,
		'{CityName}': cityName,
		'{VariantCount}': variantCount ? String(variantCount) : '',
		'{CurrentVariantName}': (() => {
			const currentVariant = allVariants.find((v: any) => v.slug === model.currentVariantSlug);
			if (!currentVariant?.name || !currentVariant?.slug) return '';
			if (!currentVariant.is_sanity_done) {
				return `<strong>${currentVariant.name}</strong>`;
			}
			return `<a href="/${brandSlug}/${modelSlug}/${currentVariant.slug}">${currentVariant.name}</a>`;
		})(),
		'{VariantsNameList}': allVariants
			.filter((v: any) => v.name && v.slug && v.slug !== model.currentVariantSlug)
			.map((v: any) => !v.is_sanity_done
				? `<strong>${v.name}</strong>`
				: `<a href="/${brandSlug}/${modelSlug}/${v.slug}">${v.name}</a>`)
			.join(', '),

		// Pricing & Estimates
		'{PriceRange}': formatPriceRange(priceRange),
		'{StartingPrice}': formatPrice(priceStart, { includeUnit: true, returnZeroOnZero: true }) as string,
		'{TopPrice}': formatPrice(priceEnd, { includeUnit: true, returnZeroOnZero: true }) as string,

		// without rupee symbol
		'{StartingPriceNoSymbol}': StartingPriceNoSymbol.replace('₹ ', ''),

		// Variant pricing
		'{StartingPriceAutomatic}': minAutomaticPrice ? formatPrice(minAutomaticPrice, { includeUnit: true, returnZeroOnZero: true }) as string : '',
		'{MaxPriceAutomatic}': maxAutomaticPrice ? formatPrice(maxAutomaticPrice, { includeUnit: true, returnZeroOnZero: true }) as string : '',
		'{StartingPriceCng}': cngStartingPrice ? formatPrice(cngStartingPrice, { includeUnit: true, returnZeroOnZero: true }) as string : '',
		'{StartingPriceDiesel}': dieselStartingPrice ? formatPrice(dieselStartingPrice, { includeUnit: true, returnZeroOnZero: true }) as string : '',
		// Top and Base variant tags
		'{TopVariantName}': topVariant?.name || '',
		'{TopVariantPrice}': formatPrice(Number(topVariant?.price ?? 0), { includeUnit: true, returnZeroOnZero: true }) as string,
		'{BaseVariantName}': baseVariant?.name || '',
		'{BaseVariantPrice}': formatPrice(Number(baseVariant?.price ?? 0), { includeUnit: true, returnZeroOnZero: true }) as string,

		// Competitors
		'{Competitor1Name}': comp1Name,
		'{Competitor1Price}': comp1Price as string,
		'{Competitor2Name}': comp2Name,
		'{Competitor2Price}': comp2Price as string,

		// Performance
		'{Mileage}': isEV ? (range || '') : (araiMileage || cityMileage || wltpMileage || ''),
		'{ARAIMileage}': araiMileage || '',
		'{CityMileage}': cityMileage || '',
		'{WLTPMileage}': wltpMileage || '',
		'{EngineType}': fuelType || '',
		'{FuelType}': isEV ? 'Electric' : (fuelType || ''),
		'{FuelOptions}': fuelOptions || '',
		'{EngineSize}': engineSize || '',
		'{MaxPower}': powerDisplay || '',
		'{MaxTorque}': maxTorque || '',
		'{Transmission}': transmissionType || '',
		'{Cylinders}': noOfCylinders || '',

		// EV specific
		'{MotorType}': fuelType || '',
		'{BatteryCapacity}': batteryCapacity || '',
		'{Range}': range || '',
		'{ChargingTime}': chargingTime || '',

		// Dimensions & Capacity
		'{BodyType}': bodyType || '',
		'{Length}': length || '',
		'{Width}': width || '',
		'{Height}': height || '',
		'{Wheelbase}': wheelBase || '',
		'{WheelSize}': wheelSize || '',
		'{TyreSize}': tyreSize || '',
		'{KerbWeight}': kerbWeight || '',
		'{GrossWeight}': grossWeight || '',
		'{SeatingCapacity}': seatingCapacity || '',
		'{BootSpace}': bootSpace || '',
		'{Doors}': noOfDoors || '',
		'{FuelTank}': fuelTankCapacity || '',
		'{GroundClearance}': groundClearanceUnladen || groundClearanceLaden || '',
		'{GroundClearanceUnladen}': groundClearanceUnladen || '',
		'{GroundClearanceLaden}': groundClearanceLaden || '',
		'{TurningRadius}': minTurningRadius || '',

		// Suspension & Brakes
		'{FrontSuspension}': frontSuspension || '',
		'{RearSuspension}': rearSuspension || '',
		'{SteeringType}': steeringType || '',
		'{FrontBrakes}': frontBrakeType || '',
		'{RearBrakes}': rearBrakeType || '',

		// Features (normalized to Yes/No or value)
		'{PowerSteering}': (String(powerSteering).toLowerCase() === 'true' || String(powerSteering) === '1') ? 'Yes' : '',
		'{PowerWindows}': (String(powerWindowsFront).toLowerCase() === 'true' || String(powerWindowsFront) === '1') ? 'Yes' : '',
		'{AC}': (String(airConditioner).toLowerCase() === 'true' || String(airConditioner) === '1') ? 'Yes' : '',
		'{AutoClimate}': (String(autoClimateControl).toLowerCase() === 'true' || String(autoClimateControl) === '1') ? 'Yes' : '',
		'{MultiSteering}': (String(multiFunctionSteering).toLowerCase() === 'true' || String(multiFunctionSteering) === '1') ? 'Yes' : '',
		'{StartStop}': (String(engineStartStop).toLowerCase() === 'true' || String(engineStartStop) === '1') ? 'Yes' : '',
		'{Tachometer}': (String(tachometer).toLowerCase() === 'true' || String(tachometer) === '1') ? 'Yes' : '',
		'{LeatherSeats}': (String(leatherSeats).toLowerCase() === 'true' || String(leatherSeats) === '1') ? 'Yes' : '',
		'{FabricSeats}': (String(fabricUpholstery).toLowerCase() === 'true' || String(fabricUpholstery) === '1') ? 'Yes' : '',
		'{DigitalCluster}': (String(digitalCluster).toLowerCase() === 'true' || String(digitalCluster) === '1') ? 'Yes' : '',
		'{AlloyWheels}': (String(alloyWheels).toLowerCase() === 'true' || String(alloyWheels) === '1') ? 'Yes' : '',
		'{FogLights}': (String(fogLightsFront).toLowerCase() === 'true' || String(fogLightsFront) === '1') ? 'Yes' : '',
		'{XenonLamps}': (String(xenonHeadlamps).toLowerCase() === 'true' || String(xenonHeadlamps) === '1') ? 'Yes' : '',
		'{ABS}': (String(abs).toLowerCase() === 'true' || String(abs) === '1') ? 'Yes' : '',
		'{DriverAirbag}': (String(driverAirbag).toLowerCase() === 'true' || String(driverAirbag) === '1') ? 'Yes' : '',
		'{PassengerAirbag}': (String(passengerAirbag).toLowerCase() === 'true' || String(passengerAirbag) === '1') ? 'Yes' : '',
		'{Airbags}': noOfAirbags ? `${noOfAirbags} Airbags` : '',
		'{CentralLocking}': (String(centralLocking).toLowerCase() === 'true' || String(centralLocking) === '1') ? 'Yes' : '',
		'{RearCamera}': rearCamera || '',
		'{Radio}': (String(radio).toLowerCase() === 'true' || String(radio) === '1') ? 'Yes' : '',
		'{Bluetooth}': (String(bluetooth).toLowerCase() === 'true' || String(bluetooth) === '1') ? 'Yes' : '',
		'{Touchscreen}': (String(touchscreen).toLowerCase() === 'true' || String(touchscreen) === '1') ? 'Yes' : '',
		'{TouchscreenSize}': touchscreenSize ? `${touchscreenSize}\"` : '',
		'{AndroidAuto}': (String(androidAuto).toLowerCase() === 'true' || String(androidAuto) === '1') ? 'Yes' : '',
		'{AppleCarPlay}': (String(appleCarplay).toLowerCase() === 'true' || String(appleCarplay) === '1') ? 'Yes' : '',
		'{AEB}': (String(aeb).toLowerCase() === 'true' || String(aeb) === '1') ? 'Yes' : '',
		'{LaneAssist}': (String(laneDeparture).toLowerCase() === 'true' || String(laneDeparture) === '1') ? 'Yes' : '',
		'{AdaptiveCruise}': (String(adaptiveCruise).toLowerCase() === 'true' || String(adaptiveCruise) === '1') ? 'Yes' : '',
		'{ConnectedCar}': (String(connectedCar).toLowerCase() === 'true' || String(connectedCar) === '1') ? 'Yes' : '',
		'{OTAUpdates}': (String(otaUpdates).toLowerCase() === 'true' || String(otaUpdates) === '1') ? 'Yes' : '',
		'{Mileage_Range}': isEV ? "Range" : "Mileage",
		'{mileage_range}': isEV ? "range" : "mileage",
		'{Year}': yearString.toString(),
		'{year}': yearString.toString(),
		'{LaunchDate}': model.launch_date ? dateFormat(model.launch_date) : ""
	};

	// ============================================
	// STEP 3: Auto-detect content type if needed
	// ============================================
	let detectedType = type;

	if (type === 'auto') {
		if (typeof content === 'string') {
			detectedType = fallback ? 'faq' : 'simple';
		} else if (typeof content === 'object') {
			// Location content: object whose keys are known conditional section names
			const locationKeys = new Set(['singleVariant', 'multiVariant', 'priceIntro', 'topVariant', 'automaticPricing', 'alternateFuel', 'closing']);
			const contentKeys = Object.keys(content);
			if (contentKeys.some(k => locationKeys.has(k))) {
				detectedType = 'location';
				// Single-section: has a top-level 'text' string property
			} else if ('text' in content && typeof content.text === 'string') {
				detectedType = 'single-section';
			} else {
				// Multi-section object (modelDynamicTopContent style)
				detectedType = 'multi-section';
			}
		}
	}

	// ============================================
	// STEP 4: Process based on type
	// ============================================

	// TYPE: SIMPLE STRING
	if (detectedType === 'simple') {
		return processSimpleTemplate(content as string, replacements);
	}

	// TYPE: FAQ WITH FALLBACK
	if (detectedType === 'faq') {
		return processFaqTemplate(content as string, replacements, carName, brand, fallback);
	}

	// TYPE: SINGLE-SECTION CONTENT (modelLocationTopContent with 'text' property)
	if (detectedType === 'single-section' && typeof content === 'object' && 'text' in content) {
		return processSingleSectionTemplate(
			content.text,
			replacements,
			removeMissingSentences
		);
	}

	// TYPE: LOCATION-SPECIFIC CONTENT (conditional priceIntro/topVariant/automaticPricing/alternateFuel)
	if (detectedType === 'location' && typeof content === 'object') {
		return processLocationContent(content, replacements, carName, brand);
	}

	// TYPE: MULTI-SECTION CONTENT (modelDynamicTopContent)
	if (detectedType === 'multi-section' && typeof content === 'object') {
		return processMultiSectionTemplate(content, replacements, carName, brand);
	}

	return '';
}

// ============================================
// HELPER FUNCTIONS FOR TAG CONVERSION
// ============================================

/**
 * Process simple template string by replacing all tags
 * 
 * @param template - Template string with tags
 * @param replacements - Map of tag to value replacements
 * @returns Processed string with tags replaced
 */
function processSimpleTemplate(
	template: string,
	replacements: Record<string, string>
): string {
	let result = template;

	// Handle price range pattern
	result = result.replace(
		/\{StartingPrice\}\s*[–-]\s*\{TopPrice\}/g,
		() => {
			const priceA = replacements['{StartingPrice}'] || '';
			const priceB = replacements['{TopPrice}'] || '';
			if (priceA && priceB) return `${priceA} – ${priceB}`;
			if (priceA) return `starting at ${priceA}`;
			if (priceB) return `up to ${priceB}`;
			return '';
		}
	);

	// Replace all tags
	for (const [tag, value] of Object.entries(replacements)) {
		if (value) {
			result = result.replace(new RegExp(tag.replace(/[{}]/g, '\\$&'), 'g'), value);
		}
	}

	// Clean up
	result = result.replace(/\{[^}]+\}/g, '');
	result = result.replace(/\s+and\s+fuel options/gi, ' fuel options');
	result = result.replace(/\s{2,}/g, ' ').trim();

	return result;
}

/**
 * Process FAQ template with fallback support
 * Falls back to fallback text if any required tag value is missing
 * 
 * @param template - Template string with tags
 * @param replacements - Map of tag to value replacements
 * @param carName - Car name for fallback
 * @param brand - Brand name for fallback
 * @param fallback - Fallback text to use if values missing
 * @returns Processed string
 */
function processFaqTemplate(
	template: string,
	replacements: Record<string, string>,
	carName: string,
	brand: string,
	fallback?: string
): string {
	// Extract all tags from template
	const tagRegex = /\{[^}]+\}/g;
	const tagsInTemplate = template.match(tagRegex) || [];

	// Check if any required value is missing (except {CarName} and {Brand})
	let hasMissingValue = false;
	for (const tag of tagsInTemplate) {
		if (tag === '{CarName}' || tag === '{Brand}' || tag === '{BrandName}') continue;

		const value = replacements[tag];
		if (!value || value === '') {
			hasMissingValue = true;
			break;
		}
	}

	// If any value missing and fallback provided (even if empty), use fallback with full tag replacement
	if (hasMissingValue && fallback !== undefined) {
		return processSimpleTemplate(fallback, replacements);
	}

	// Otherwise do normal replacement
	return processSimpleTemplate(template, replacements);
}

/**
 * Process single-section content (location route)
 * Optionally removes sentences with unfilled tags
 * 
 * @param text - Text content with tags
 * @param replacements - Map of tag to value replacements
 * @param removeMissingSentences - Whether to remove sentences with unfilled tags
 * @returns Processed string
 */
function processSingleSectionTemplate(
	text: string,
	replacements: Record<string, string>,
	removeMissingSentences: boolean
): string {
	let result = text;

	// Replace all tags
	for (const [tag, value] of Object.entries(replacements)) {
		if (value) {
			result = result.replace(new RegExp(tag.replace(/[{}]/g, '\\$&'), 'g'), value);
		}
	}

	// Remove sentences with unreplaced tags (for location route)
	if (removeMissingSentences) {
		const sentences = result.split('. ');
		const validSentences = sentences.filter(sentence => {
			return !sentence.match(/\{[^}]+\}/);
		});
		result = validSentences.join('. ');
	}

	// Clean up
	result = result.replace(/\{[^}]+\}/g, '');
	result = result.replace(/\s{2,}/g, ' ').trim();

	return result;
}

/**
 * Process multi-section content (brand/model route)
 * Handles fallback for each section individually
 * 
 * @param content - Object with multiple sections, each with text and optional fallback
 * @param replacements - Map of tag to value replacements
 * @param carName - Car name for fallback
 * @param brand - Brand name for fallback
 * @returns Combined processed string from all sections
 */
function processMultiSectionTemplate(
	content: any,
	replacements: Record<string, string>,
	carName: string,
	brand: string
): string {
	const sections = [];

	for (const [key, section] of Object.entries(content)) {
		if (!section || typeof section !== 'object') continue;

		const { text, fallback } = section as { text: string; fallback: string };
		if (!text) continue;

		// Special handling for specific sections
		if (key === 'transmissionWheelbase') {
			if (!replacements['{Transmission}'] || !replacements['{WheelSize}']) {
				if (fallback !== undefined) sections.push(fallback);
				continue;
			}
		}

		if (key === 'motorBatteryPower') {
			if (!replacements['{MotorType}'] || !replacements['{BatteryCapacity}'] || !replacements['{MaxPower}']) {
				if (fallback !== undefined) sections.push(fallback);
				continue;
			}
		}

		// Check if any required values are missing
		const tagRegex = /\{[^}]+\}/g;
		const tagsInText = text.match(tagRegex) || [];

		let hasMissingValue = false;
		for (const tag of tagsInText) {
			if (tag === '{CarName}' || tag === '{Brand}' || tag === '{BrandName}') continue;

			const value = replacements[tag];
			if (!value || value === '') {
				hasMissingValue = true;
				break;
			}
		}

		// Use fallback if missing values (even if empty), otherwise process text
		if (hasMissingValue && fallback !== undefined) {
			sections.push(processSimpleTemplate(fallback, replacements));
		} else {
			let result = text;

			// Handle price range pattern
			result = result.replace(
				/\{StartingPrice\}\s*[–-]\s*\{TopPrice\}/g,
				() => {
					const priceA = replacements['{StartingPrice}'];
					const priceB = replacements['{TopPrice}'];
					if (priceA && priceB) return `${priceA} – ${priceB}`;
					if (priceA) return `starting at ${priceA}`;
					if (priceB) return `up to ${priceB}`;
					return '';
				}
			);

			// Replace all tags
			for (const [tag, value] of Object.entries(replacements)) {
				if (value) {
					result = result.replace(new RegExp(tag.replace(/[{}]/g, '\\$&'), 'g'), value);
				}
			}

			// Clean up
			result = result.replace(/\{[^}]+\}/g, '');
			result = result.replace(/\s{2,}/g, ' ').trim();

			if (result) sections.push(result);
		}
	}

	return sections.join(' ');
}

/**
 * Process location-specific content with complex conditional rules
 * 
 * @param content - Multi-section location content object
 * @param replacements - Map of tag to value replacements
 * @param carName - Car name for fallback
 * @param brand - Brand name for fallback
 * @returns Combined processed string from all sections
 */
function processLocationContent(
	content: any,
	replacements: Record<string, string>,
	carName: string,
	brand: string
): string {
	const sections = [];
	const parsedVariantCount = Number(replacements['{VariantCount}'] || 0);
	const isSingleVariant = parsedVariantCount <= 1;

	// Process each section with specific logic
	for (const [key, section] of Object.entries(content)) {
		if (!section || typeof section !== 'object') continue;

		if (key === 'singleVariant') {
			const { text, fallback } = section as { text: string; fallback?: string };
			if (!isSingleVariant) continue;

			const hasOnRoadPrice = Boolean(replacements['{OnRoadPrice}']);
			if (hasOnRoadPrice) {
				sections.push(processSimpleTemplate(text, replacements));
			} else if (fallback !== undefined) {
				sections.push(processSimpleTemplate(fallback, replacements));
			}
			continue;
		}

		if (key === 'multiVariant') {
			const { text, fallback } = section as { text: string; fallback?: string };
			if (isSingleVariant) continue;

			const hasOnRoadRange = Boolean(replacements['{OnRoadPrice}']);
			if (hasOnRoadRange) {
				sections.push(processSimpleTemplate(text, replacements));
			} else if (fallback !== undefined) {
				sections.push(processSimpleTemplate(fallback, replacements));
			}
			continue;
		}

		// SECTION: Price Intro
		if (key === 'priceIntro') {
			const { text, fallback } = section as { text: string; fallback: string };

			const price = replacements['{StartingPrice}'] ?? '';
			const topPrice = replacements['{TopVariantPrice}'] || '';

			const priceStr = String(price).trim();

			const hasRealPrice =
				priceStr !== '' &&
				priceStr !== '0' &&
				priceStr?.toLowerCase() !== 'price on request';

			const hasRealTopPrice =
				topPrice !== '' &&
				topPrice !== '0'

			// remove -  "and go up to {TopVariantPrice}"
			let content = text;
			if (!hasRealTopPrice) {
				content = text.replace(/ and go up to \{TopVariantPrice\}/, '');
			}

			if (hasRealPrice) {
				sections.push(processSimpleTemplate(content, replacements));
			} else if (fallback !== undefined) {
				sections.push(processSimpleTemplate(fallback, replacements));
			}

			continue;
		}

		// SECTION: Top Variant
		if (key === 'topVariant') {
			const { text, fallback } = section as { text: string; fallback?: string };
			const hasTopVariant = replacements['{TopVariantName}'] &&
				replacements['{TopVariantPrice}'] &&
				replacements['{TopVariantPrice}'] !== 'Rs 0' &&
				replacements['{TopVariantPrice}'] !== '';

			if (hasTopVariant) {
				sections.push(processSimpleTemplate(text, replacements));
			} else if (fallback !== undefined) {
				sections.push(processSimpleTemplate(fallback, replacements));
			}
			continue;
		}

		// SECTION: Automatic Pricing
		if (key === 'automaticPricing') {
			const { textRange, textSingle } = section as { textRange: string; textSingle: string; fallback: string };
			const minAuto = replacements['{StartingPriceAutomatic}'];
			const maxAuto = replacements['{MaxPriceAutomatic}'];

			if (minAuto && maxAuto && minAuto !== maxAuto) {
				// Range available
				sections.push(processSimpleTemplate(textRange, replacements));
			} else if (minAuto && minAuto !== '') {
				// Single price
				sections.push(processSimpleTemplate(textSingle, replacements));
			}
			// Otherwise skip this section
			continue;
		}

		// SECTION: Alternate Fuel (CNG or Diesel)
		if (key === 'alternateFuel') {
			const { textCng, textDiesel } = section as { textCng: string; textDiesel: string; fallback: string };
			const hasCng = replacements['{StartingPriceCng}'] && replacements['{StartingPriceCng}'] !== '';
			const hasDiesel = replacements['{StartingPriceDiesel}'] && replacements['{StartingPriceDiesel}'] !== '';

			if (hasCng) {
				// CNG takes priority
				sections.push(processSimpleTemplate(textCng, replacements));
			} else if (hasDiesel) {
				// Fallback to Diesel
				sections.push(processSimpleTemplate(textDiesel, replacements));
			}
			// Otherwise skip this section
			continue;
		}

		// SECTION: Additional Cost or Variant Details
		// These typically trigger fallback if price is missing, even if NO tags are missing
		if (key === 'additionalCost' || key === 'variantDetails' || key === 'competitorComparison') {
			const { text, fallback } = section as { text: string; fallback?: string };
			const price = replacements['{StartingPrice}'] ?? '';
			const hasPrice = price !== '' && price !== '0' && price.toLowerCase() !== 'price on request';
			const topPrice = replacements['{TopVariantPrice}'] || '';
			const hasTopPrice = topPrice !== '' && topPrice !== '0';
			let content = text;
			if (!hasTopPrice && key === 'variantDetails') {
				content = text.replace(/, while the top variant {TopVariantName} may be priced at {TopVariantPrice}/, '');
			}
			if (hasPrice) {
				sections.push(processSimpleTemplate(content, replacements));
			} else if (fallback !== undefined) {
				sections.push(processSimpleTemplate(fallback, replacements));
			}
			continue;
		}

		// SECTION: Launch Date (for upcoming cars)
		if (key === 'launchDate') {
			const { text, fallback } = section as { text: string; fallback?: string };
			const launchDate = replacements['{LaunchDate}'] ?? '';
			const hasLaunchDate = launchDate !== '' && launchDate !== '0';

			if (hasLaunchDate) {
				sections.push(processSimpleTemplate(text, replacements));
			} else if (fallback !== undefined) {
				sections.push(processSimpleTemplate(fallback, replacements));
			}
			continue;
		}

		// SECTION: Closing or other sections
		const { text, fallback } = section as { text: string; fallback?: string };
		if (!text) continue;

		const tagRegex = /\{[^}]+\}/g;
		const tagsInText = text.match(tagRegex) || [];

		let hasMissingValue = false;
		for (const tag of tagsInText) {
			if (tag === '{CarName}' || tag === '{Brand}' || tag === '{BrandName}') continue;

			const value = replacements[tag];
			if (!value || value === '') {
				hasMissingValue = true;
				break;
			}
		}

		if (!hasMissingValue) {
			sections.push(processSimpleTemplate(text, replacements));
		} else if (fallback !== undefined) {
			sections.push(processSimpleTemplate(fallback, replacements));
		}
	}

	return sections.join(' ');
}
